import React, { useEffect, useState } from "react";
import OrderSummaryList from "../../../../components/sections/front/OrderSummaryList";
import OrderSummaryInfo from "../../../../components/sections/front/OrderSummaryInfo";
import Button from "../../../../components/form/Button";
import { eventDetails } from "../../../../services/eventService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import {
  checkTicketExistence,
  referVerificationDetails,
  ticketList,
} from "../../../../services/ticketService";
import { toast } from "react-toastify";
import { lngDetect } from "../../../../helpers/lngDetect";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const TicketsOrderSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { eventPromoter } = useOutletContext();

  const { eventId } = useParams();
  const { lang } = useSelector((state) => state?.langProvider);
  const { data } = useSelector((state) => state?.profile);
  const [checkoutData, setCheckoutData] = useState([]);
  const location = useLocation();
  const queryObject = Object.fromEntries(new URLSearchParams(location.search));
  const {
    event: eventDetailsData,
    eventLoading,
    eventOrganizer,
  } = useOutletContext();

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ["ticket"] });
    };
  }, []);

  const {
    data: ticket,
    isLoading: ticketLoading,
    isFetching: ticketFetching,
  } = useQuery({
    queryKey: ["ticket"],
    queryFn: async () => {
      const res = await ticketList({
        sortQuery: "ordering",
        eventUid: eventId,
        limit: 1000,
        visibility: "visible",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return {
        ...res?.docs,
        data: res?.docs?.data?.map((item) => {
          const find_discount =
            eventPromoter?.data?.data?.info?.settingsPerTicket?.find(
              (elm) =>
                elm?.ticket?.id === item?._id || elm?.ticket?._id === item?._id
            );

          const discount_type = find_discount?.discount?.type;
          const discount_value = Number(find_discount?.discount?.value) || 0;

          let finalPrice = item?.price;

          if (discount_type && discount_value) {
            if (discount_type === "percentage") {
              finalPrice = item?.price - item?.price * discount_value; // e.g. 0.1 = 10%
            } else if (discount_type === "fixed") {
              finalPrice = item?.price - discount_value;
            }

            // prevent negative price
            if (finalPrice < 0 || !finalPrice) finalPrice = 0;
          }

          return {
            ...item,
            id: item?._id,
            title: lngDetect(item?.name),
            price: finalPrice,
            originalPrice: item?.price,
            // fee: "₹50",
            info: "Access to all sessions & keynote",
          };
        }),
      };
    },
    ...(queryObject?.referral_code
      ? { enabled: !!eventPromoter?.data?.data?._id && !!eventId }
      : {
          enabled: !!eventId,
        }),
  });

  const { mutate: checkoutHandler, isPending } = useMutation({
    mutationFn: async (val) => {
      if (
        eventOrganizer?.data?.setting?.bannedEmail?.some((email) =>
          email.includes(data?.email)
        )
      ) {
        navigate(`/${lang}/accessDenied`, {
          state: {
            eventId,
            message: lngDetect(
              eventOrganizer?.data?.setting?.bannedEmailMessage
            ),
            route: `/${lang}/order-summary/${eventId}`,
          },
        });
        return;
      }
      const res = await checkTicketExistence({
        items: checkoutData?.map((item) => ({
          ticket: item?._id,
          quantity: item?.buy_quentity,
          attendeeInfo: val,
        })),
      });
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        navigate(`/${lang}/checkout/${eventId}`, {
          state: {
            checkoutData,
            ...(queryObject?.referral_code && {
              promoterUid: queryObject?.referral_code,
            }),
          },
        });
      } else {
        setCheckoutData(
          checkoutData?.map((ck_item) => {
            const find_ticket = res?.data?.ticketData?.find(
              (errTicket) => errTicket?._id === ck_item?._id
            );
            const ticket_left =
              find_ticket?.quantity - find_ticket?.quantitySold;
            const buy_quentity = ck_item?.buy_quentity;
            if (
              find_ticket?._id === ck_item?._id &&
              buy_quentity > ticket_left
            ) {
              const checkout_data = {
                ...ck_item,
                buy_quentity: ticket_left,
                totalPrice: ticket_left * find_ticket?.price,
              };
              return checkout_data;
            } else {
              return ck_item;
            }
          })
        );
        queryClient.setQueryData(["ticket"], (state) => {
          const state_data = JSON.parse(JSON.stringify(state || {}));
          state_data.data = state_data?.data?.map((item) => {
            const find_ticket = res?.data?.ticketData?.find(
              (errTicket) => errTicket?._id === item?._id
            );
            return find_ticket ? find_ticket : item;
          });
          return { ...state_data };
        });
      }
    },
    onError: (err) => {},
  });

  function ticketBuyHandler({ val, ticket_left, is_all_sold, ticket }) {
    if (val === "reset") {
      setCheckoutData((pre) => {
        pre = pre?.filter((item) => item?._id !== ticket?._id);
        return [...pre];
      });
      return;
    }
    const min_val = ticket?.quantityMin;
    const max_val = ticket?.quantityMax;
    if (val < min_val) {
      toast.info(`Must buy min ${min_val} ticket`);
    } else if (val > max_val) {
      toast.info(`Maximum you can buy ${max_val} ticket`);
    }
    const accepted_value =
      val < min_val ? min_val : val > max_val ? max_val : val;
    setCheckoutData((pre) => {
      if (accepted_value > 0) {
        const tickey_buy =
          accepted_value > ticket_left ? ticket_left : accepted_value;
        if (pre?.some((item) => item?._id === ticket?._id)) {
          pre = pre.map((item) =>
            item?._id === ticket?._id
              ? {
                  ...item,
                  buy_quentity: tickey_buy,
                  totalPrice: tickey_buy * item?.price,
                }
              : item
          );
        } else {
          pre = [
            ...pre,
            {
              ...ticket,
              buy_quentity: tickey_buy,
              totalPrice: tickey_buy * ticket?.price,
            },
          ];
        }
      } else {
        pre = pre?.filter((item) => item?._id !== ticket?._id);
      }
      return [...pre];
    });
  }

  if (!eventDetailsData?.data?._id && !eventLoading) {
    navigate(`/${lang}/events`);
    return;
  }

  return (
    <>
      <section className="relative pt-10 md:pt-12 pb-24 bg-magnolia">
        <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl w-full mx-auto relative px-5 xl:px-0">
          <h1 className="text-2xl md:text-3xl text-deep-purple uppercase font-bold mb-2">
            {lngDetect(eventDetailsData?.data?.title)}
          </h1>
          <div className="text-sm md:text-base text-slate-600 mb-5 flex items-center gap-2">
            {dayjs(eventDetailsData?.data?.startDate).format(
              "dddd, MMM hh:mm A"
            )}
            <span>
              <i className="fa-fw fa-regular fa-minus"></i>
            </span>
            {dayjs(eventDetailsData?.data?.endDate).format("dddd, MMM hh:mm A")}
          </div>
          <div className="text-xl md:text-2xl font-bold capitalize mb-3">
            {t("Tickets")}
          </div>
          <div className="relative flex flex-col md:flex-row gap-6">
            <div className="relative flex-shrink w-full md:w-1/2">
              <div className="space-y-3">
                {ticketLoading ? (
                  <div>{t("Loading")}...</div>
                ) : (
                  (ticket?.data || []).map((ticket) => {
                    const ticket_left =
                      (ticket.quantity || 0) - (ticket?.quantitySold || 0);
                    const is_all_sold =
                      ticket_left >= 0
                        ? (ticket.quantity || 0) === (ticket?.quantitySold || 0)
                        : true;
                    const buy_quentity =
                      checkoutData
                        ?.filter((item) => item?._id === ticket?._id)
                        ?.at(-1)?.buy_quentity || 0;

                    return (
                      <OrderSummaryList
                        key={ticket?.id}
                        data={ticket}
                        value={buy_quentity > 0 ? buy_quentity : 0}
                        allSold={is_all_sold}
                        ticketLeft={ticket_left - buy_quentity}
                        setValue={(val) => {
                          ticketBuyHandler({
                            val,
                            ticket_left,
                            is_all_sold,
                            ticket,
                          });
                        }}
                      />
                    );
                  })
                )}
              </div>
            </div>
            <div className="relative flex-shrink-0 w-full md:w-1/2">
              <OrderSummaryInfo checkoutData={checkoutData} />
              <div className="flex justify-end">
                {checkoutData?.reduce(
                  (acc, cur) => acc + cur?.buy_quentity,
                  0
                ) > 0 && (
                  <div className="flex justify-end mt-5">
                    <Button
                      buttonLabel={t("checkout")}
                      buttonFunction={() => {
                        checkoutHandler();
                      }}
                      {...(isPending && {
                        loading: true,
                        buttonIconPosition: "left",
                        isDisable: true,
                      })}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default TicketsOrderSummary;
